import { Notification } from '@thanx/uikit/notification'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback } from 'react'
import ConfigModalHost from './ConfigModalHost'
import { RowKey, ROW_MODAL_WIDTH } from './configRowKeys'
import type {
  ConfigRowLoadState,
  ConfigRowSaveAdapter,
} from './configRowSaveAdapter'

// Row label and dialog title resolve from the same settings-tab labels, so a row
// can never be titled differently from the chevron that opened it.
const tTabs = buildTranslate('recovery_ai.settings.tabs')
const tModal = buildTranslate('recovery_ai.config_modal')

type Props = {
  rowKey: RowKey
  /** Save surface. Draft rows pass RecoveryConfigDraftValue itself (no shim). */
  adapter: ConfigRowSaveAdapter
  /** Load surface, kept separate from `adapter` so the no-shim property holds. */
  load: ConfigRowLoadState
  /** Overrides the inline save-failure copy (active_locations narrows it). */
  saveErrorMessage?: string
  children: React.ReactNode
  onClose: () => void
  onSaved: () => void
  /** Omit to drop the button entirely, leaving a Cancel/Save footer. */
  onSaveAndTest?: () => void
  /** Extra gating on Save-and-test; the load guard below is applied regardless. */
  canSaveAndTest?: boolean
  /** Non-save work in flight (derive / regenerate) — blocks dismiss and
   *  Save-and-test, but not plain Save (disjoint resources; see ConfigModalHost). */
  isBusy?: boolean
  /**
   * Overrides the row's default dialog width. The activation summary passes one
   * (its Figma is much wider than the Settings column these rows were sized for);
   * every other host omits it and keeps ROW_MODAL_WIDTH.
   */
  width?: string
  /**
   * Overrides the footer's Save-button copy (defaults to "Save"). The
   * Agent-Tuning host passes "Save changes" for approved_rewards (Figma
   * parity); every other host/row omits it and keeps the default.
   */
  saveLabel?: string
  /** Overrides the tab-derived title; omit to keep tTabs(rowKey). */
  title?: string
}

// The chrome every config-modal row shares: title, width, the load/load-error
// gate that withholds the editing body, the inline save-failure banner, and the
// one save code path (Save closes ONLY on a successful commit; Cancel / X /
// scrim roll back and close, so dismissing can never persist a staged edit).
//
// context: takes `children`, never a `body` prop — the per-row adapter hooks
// have to live in per-row components, and hooks cannot be conditional.
const ConfigRowModal = ({
  rowKey,
  adapter,
  load,
  saveErrorMessage,
  children,
  onClose,
  onSaved,
  onSaveAndTest,
  canSaveAndTest = true,
  isBusy,
  width,
  saveLabel,
  title,
}: Props) => {
  const { canManage, isSaving, isSaveError, commit, rollback } = adapter
  const { isLoading, isLoadError } = load

  // NEITHER footer control may act from a still-loading or errored modal: the
  // editing body is withheld in both states (below), so anything they commit is
  // derived from data the merchant cannot see. Shared so the two cannot drift.
  //
  //   context: active_locations is what makes this load-bearing rather than
  //   tidy. Its isDirty carries a server-derived term (defaultDirty), so a
  //   failed — or merely in-flight — location_configs GET leaves it dirty on
  //   ZERO clicks, and one Save then writes new_location_default_enabled:false
  //   derived from an empty list, over the error banner, reported as success.
  //   The spinner half is not hypothetical: on the activation summary `config`
  //   is already cached while the locations GET is still in flight, so Save is
  //   armed on every open for a 100%-coverage merchant. Keep BOTH terms.
  const isRowReady = !isLoading && !isLoadError

  // `canManage` is not belt-and-braces on either control: both route through the
  // SAME commit(), so gating one leaves the other armed for a view-only user.
  //
  //   context: only the active_locations and escalation_contacts panes carried a
  //   permission term on their own Save; the other four rows' panes gate on
  //   `isDirty && !isSaving` (Settings/useSaveAndTest.ts) and never did. So this
  //   is a NEW floor for those rows, not a preserved one — do not "restore
  //   parity" by removing it. No row passes canSaveAndTest explicitly, so
  //   without that term it is simply `true` on any loaded row.
  //
  //   Save is deliberately NOT additionally gated on isDirty. A merchant who
  //   edits the auto-saved make-good ladder (never staged — see AgentTuning-
  //   ConfigModals) then finds Save disabled because nothing else changed reads
  //   as broken, not safe. Every ConfigRowSaveAdapter implementation already
  //   treats a clean commit() as a no-op that resolves true without a PUT
  //   (configRowSaveAdapter.ts), so dropping isDirty here only changes when
  //   Save is CLICKABLE — a no-op click still writes nothing and just closes.
  const canSaveNow = canManage && isRowReady
  const canSaveAndTestNow = canSaveAndTest && canManage && isRowReady

  const handleSave = useCallback(async () => {
    // Guarded like handleSaveAndTest below — `disabled` is a host render detail,
    // and this is the write path isRowReady exists to protect.
    if (!canSaveNow) return
    // A failed PUT keeps the modal open so the merchant sees the error and
    // retains the edits (commit does not clear staged state on failure).
    if (await commit()) onSaved()
  }, [canSaveNow, commit, onSaved])

  const handleSaveAndTest = useCallback(async () => {
    if (!canSaveAndTestNow) return
    if (await commit()) onSaveAndTest?.()
  }, [canSaveAndTestNow, commit, onSaveAndTest])

  const handleCancel = useCallback(() => {
    rollback()
    onClose()
  }, [rollback, onClose])

  let body: React.ReactNode = children
  if (isLoading) {
    // Withhold the editing body so the merchant never edits — or Saves over —
    // defaults shown before their real data loads.
    body = (
      <div role="status" aria-label={tModal('loading')}>
        <Spinner isLoading size="4x" className="text-center" />
      </div>
    )
  } else if (isLoadError) {
    body = (
      <Notification kind="negative" size="full">
        {tModal('load_error')}
      </Notification>
    )
  }

  return (
    <ConfigModalHost
      isOpen
      title={title ?? tTabs(rowKey)}
      onSave={handleSave}
      // Omitting the handler is what drops the button (ConfigModalHost renders
      // it only when wired), leaving a Cancel/Save footer.
      onSaveAndTest={onSaveAndTest ? handleSaveAndTest : undefined}
      onCancel={handleCancel}
      // Three of the six rows (recovery_generosity, approved_rewards,
      // brand_voice) carry NO ability check in their body, and active_locations
      // is dirty from server data alone — see canSaveNow above for both terms.
      canSave={canSaveNow}
      // Disabled rather than a no-op handler, so it never looks clickable-but-dead.
      canSaveAndTest={canSaveAndTestNow}
      isSaving={isSaving}
      isBusy={isBusy}
      // A host override wins; otherwise the row default, which is undefined for
      // every row but active_locations — that omission is how the shipped
      // Agent-Tuning modals keep ModalContainer's 600px byte-for-byte (L30).
      width={width ?? ROW_MODAL_WIDTH[rowKey]}
      saveLabel={saveLabel}
    >
      {/* Inline save-failure banner — the modal stays open with edits intact. */}
      {isSaveError && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {saveErrorMessage ?? tModal('save_error')}
          </Notification>
        </div>
      )}
      {body}
    </ConfigModalHost>
  )
}

export default ConfigRowModal
